'use client';

import type { FC, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import '../styles/main.css';
import { ConfigProvider } from 'antd';
import light from '@/styles/themes/light';
import { Provider, useDispatch } from 'react-redux';
import type { AppDispatch } from '@/redux/store';
import store from '@/redux/store';
import { HeaderProvider } from '@/contexts/HeaderContext';
import { MessageRefreshProvider } from '@/contexts/MessageRefreshContext';
import { ClerkProvider, useAuth, useUser } from '@clerk/nextjs';
import ProtectedRoute from '@/components/Route/ProtectedRoute';
import { TolgeeNextProvider } from '@/tolgee/client';
import { ALL_LOCALES, getStaticData } from '@/tolgee/shared';
import type { TolgeeStaticData } from '@tolgee/react';
import { fetchUserData } from '@/redux/reducers/userSlice';
import { useTolgee, useTranslate } from '@tolgee/react';
import { usePathname, useRouter } from 'next/navigation';
import { LANGUAGE_KEY, setLanguage } from '@/redux/reducers/languageSlice';
import { getUserLocale, normalizeLocale } from '@/utils/helpers';
import { sharedConfig } from '@/config/sharedConfig';
import { ROUTES } from '@/config/routes';
import { PUBLIC_ROUTES } from '@/config/publicRoutes';
import Loader from '@/components/Loader/Loader';
import type { UserDataPayload } from '@/services/userService';
import { ToastProvider } from '@/hooks/useToast';
import { PageConfigProvider } from '@/app/design-system/hooks/usePageConfig';
import { isGinioneDomain } from '@/utils/clerkApp';

const AuthRoutingWrapper: FC<{ children: ReactNode }> = ({ children }) => {
  const { user, isSignedIn } = useUser();
  const { getToken } = useAuth();
  const dispatch: AppDispatch = useDispatch();
  const tolgee = useTolgee();
  const router = useRouter();
  const pathname = usePathname();
  const [isRoutingComplete, setIsRoutingComplete] = useState(false);
  const [_userData, setUserData] = useState<UserDataPayload | null>(null);
  const [_isLoadingUserData, setIsLoadingUserData] = useState(true);

  // Determine initial routing destination based on domain and user access.
  // Domain determines the product: ginione.de → OHS Hub, getbloom.work → EAP.
  const getInitialRoute = (userData: UserDataPayload, currentRoute: string): string | null => {
    const hasEapAccess = userData.organization?.hasEapAccess;
    const hasOhsHubAccess = userData.organization?.hasOhsHubAccess && userData.hasOhsHubAccess;

    if (isGinioneDomain()) {
      if (!hasOhsHubAccess) return ROUTES.UNAUTHORIZED;
      // Redirect bare "/" to OHS Hub on ginione.de
      if (currentRoute === ROUTES.HOME) return ROUTES.OHS_HUB;
      return null;
    }

    // Bloom domain
    if (!hasEapAccess) {
      // User with only OHS Hub access landing on "/" → send them to OHS Hub
      // (covers localhost dev without /etc/hosts, and prod edge cases)
      if (hasOhsHubAccess && currentRoute === ROUTES.HOME) return ROUTES.OHS_HUB;
      return null; // let hasAccessToCurrentRoute handle specific routes
    }
    return null;
  };

  // Check if user has access to current route
  const hasAccessToCurrentRoute = (userData: UserDataPayload, currentRoute: string): boolean => {
    const isPublicRoute = PUBLIC_ROUTES.some((route) => {
      if (route.endsWith('*')) {
        return currentRoute.startsWith(route.slice(0, -1));
      }
      return currentRoute === route;
    });
    if (isPublicRoute) {
      return true;
    }

    if (currentRoute.startsWith(ROUTES.ADMIN)) {
      return userData.role === 'admin' || userData.role === 'ops';
    }

    if (currentRoute.startsWith(ROUTES.OHS_HUB)) {
      return !!(userData.organization?.hasOhsHubAccess && userData.hasOhsHubAccess);
    }

    if (userData.organization?.hasEapAccess) {
      return true;
    }

    return false;
  };

  useEffect(() => {
    const initializeUser = async () => {
      if (!isSignedIn || !user) {
        setIsRoutingComplete(true);
        setIsLoadingUserData(false);
        return;
      }

      try {
        setIsLoadingUserData(true);
        const token = await getToken({ template: 'Staging' });
        if (!token) {
          console.warn('Clerk token not available despite isSignedIn=true.');
          setIsRoutingComplete(true);
          setIsLoadingUserData(false);
          void router.replace(ROUTES.SIGN_IN);
          return;
        }

        const fetchedUserData = await dispatch(
          fetchUserData({ authUserId: user.id, token }),
        ).unwrap();

        if (!fetchedUserData) {
          console.error('Fetched application user data is null/undefined.');
          setUserData(null);
          setIsLoadingUserData(false);
          setIsRoutingComplete(true);
          void router.replace(ROUTES.SIGN_IN);
          return;
        }

        setUserData(fetchedUserData);

        if (fetchedUserData.language) {
          dispatch(setLanguage(fetchedUserData.language));
          void tolgee.changeLanguage(normalizeLocale(fetchedUserData.language));
        }

        const currentRoute = pathname || '';

        // Domain-based routing: check initial route redirect (e.g. "/" → "/ohs-hub" on ginione.de)
        const initialRoute = getInitialRoute(fetchedUserData, currentRoute);
        if (initialRoute && initialRoute !== currentRoute) {
          void router.replace(initialRoute);
        } else if (!hasAccessToCurrentRoute(fetchedUserData, currentRoute)) {
          void router.replace(ROUTES.UNAUTHORIZED);
        }

        setIsLoadingUserData(false);
        setIsRoutingComplete(true);
      } catch (error: unknown) {
        console.error(
          'AuthRoutingWrapper: fetchUserData or subsequent logic failed:',
          error instanceof Error ? error.message : String(error),
        );
        setUserData(null);
        setIsLoadingUserData(false);
        setIsRoutingComplete(true);
        void router.replace(ROUTES.SIGN_IN);
      }
    };

    void initializeUser();
  }, [dispatch, getToken, isSignedIn, user, router, tolgee, pathname]);

  // Show loader if routing is not complete
  return isRoutingComplete ? children : <Loader position="absolute" />;
};

const RootLayout: FC<{ children?: ReactNode }> = ({ children }) => {
  const [locale, setLocale] = useState<string>(sharedConfig.defaultLocale);
  const [locales, setLocales] = useState<TolgeeStaticData>({});
  const [publishableKey, setPublishableKey] = useState<string | null>(null);

  useEffect(() => {
    // Determine Clerk app based on hostname (must run client-side)
    const isGinione =
      process.env.NEXT_PUBLIC_ENABLE_DUAL_CLERK !== 'false' &&
      window.location.hostname.includes('ginione.de');
    const key = isGinione
      ? process.env.NEXT_PUBLIC_CLERK_GINIONE_PUBLISHABLE_KEY
      : process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY;

    if (!key) {
      console.error(
        `Missing Clerk publishable key for ${isGinione ? 'Ginione' : 'Bloom'}. ` +
          `Check NEXT_PUBLIC_CLERK_${isGinione ? 'GINIONE_' : ''}PUBLISHABLE_KEY env var.`,
      );
    }

    setPublishableKey(key ?? process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY ?? null);
  }, []);

  useEffect(() => {
    const savedLocale = localStorage.getItem(LANGUAGE_KEY);
    if (!savedLocale) {
      const browserLang = getUserLocale();
      localStorage.setItem(LANGUAGE_KEY, browserLang);
      setLocale(browserLang);
    } else {
      setLocale(savedLocale);
    }

    const loadLocales = async () => {
      const loadedLocales = await getStaticData(ALL_LOCALES);
      setLocales(loadedLocales);
    };

    void loadLocales();
  }, []);

  // Wait for client-side key resolution before rendering ClerkProvider
  if (!publishableKey) {
    return (
      <html lang={locale}>
        <head>
          <link rel="icon" href="/api/favicon" />
        </head>
        <body className="flex h-screen">
          <Loader position="absolute" />
        </body>
      </html>
    );
  }

  return (
    <html lang={locale}>
      <head>
        <link rel="icon" href="/api/favicon" />
      </head>
      <body className="flex h-screen">
        <Provider store={store}>
          <ConfigProvider theme={light}>
            <ClerkProvider publishableKey={publishableKey}>
              <HeaderProvider>
                <PageConfigProvider>
                  <MessageRefreshProvider>
                    <ProtectedRoute>
                      {Object.keys(locales).length > 0 && (
                        <TolgeeNextProvider locale={locale} locales={locales}>
                          <AuthRoutingWrapper>
                            <ToastProvider>
                              <PageTitle />
                              {children}
                            </ToastProvider>
                          </AuthRoutingWrapper>
                        </TolgeeNextProvider>
                      )}
                    </ProtectedRoute>
                  </MessageRefreshProvider>
                </PageConfigProvider>
              </HeaderProvider>
            </ClerkProvider>
          </ConfigProvider>
        </Provider>
      </body>
    </html>
  );
};

const PageTitle = () => {
  const { t } = useTranslate();
  const pathname = usePathname();

  useEffect(() => {
    const isExpertRoute = pathname?.startsWith('/expert');
    document.title = isExpertRoute
      ? t('expert_dashboard.page.title.text')
      : t('eap.page.title.text');
  }, [pathname, t]);

  return null;
};

export default RootLayout;
